/* eslint-disable max-len */
// import styles from './Footer.module.scss';
import React, { useEffect, useState } from "react";
import {
  Button,
  Input,
  InputSelect,
  DatePickerInput,
  PhoneInput,
} from "../../../components";
import {
  EmailVerificationModal,
  EmailInUseModal,
} from "../../../components/modals";
import styles from "../RegisterPage.module.scss";
import { Formik } from "formik";
import ReCAPTCHA from "react-google-recaptcha";
import map from "lodash/map";
import { useDispatch, useSelector } from "react-redux";
import { useQueryParam } from "../../../hooks/useQueryParam";
import {
  appSelector,
  resendEmailCode,
  submitEmailVerification,
} from "@podcasts/store/application";
import { firstApplicationStepSchema } from "@podcasts/schemas";
import { countriesList, genderSelectOptions } from "@podcasts/constants";

const countries = map(countriesList, (v) => ({
  value: v.isoCode,
  label: v.name,
}));

export function FirstStep({ initialValues, onNext }: any) {
  const dispatch = useDispatch();
  const { user, error: registerErrorMessage } = useSelector(appSelector) as any;
  const [isEmailInUseModalOpen, setIsEmailInUseModalOpen] = useState(false);
  const [emailVerificationError, setEmailVerificationError] =
    useState<string>();
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [isDisabledVerifyButton, setIsDisabledVerifyButton] = useState(false);
  const referralToken = useQueryParam("referral");

  useEffect(() => {
    if (
      registerErrorMessage &&
      registerErrorMessage === "Email is already taken."
    ) {
      setIsEmailInUseModalOpen(true);
    }
  }, [registerErrorMessage]);

  // Check if email is not verified
  useEffect(() => {
    if (user?.email_verified === false) {
      setIsVerificationModalOpen(true);
    } else if (user?.email_verified === true) {
      setIsVerificationModalOpen(false);
    }
  }, [user]);

  const handleVerifyEmailCode = async (code: string) => {
    const data = await dispatch(submitEmailVerification({ code }));
    const error = data?.payload?.error;
    if (error === "too_many_attempts") {
      setEmailVerificationError(
        "You have exceeded the maximum number of attempts. Please click on “Resend” to try again.",
      );
      setIsDisabledVerifyButton(true);
    } else if (error === "expired_code") {
      setEmailVerificationError(
        "The code you entered has expired. Please click on “Resend” to try again.",
      );
      setIsDisabledVerifyButton(true);
    } else {
      setEmailVerificationError(
        "The code you entered is invalid. Please try again",
      );
    }
  };

  const handleResendEmailCode = async () => {
    const response = await dispatch(resendEmailCode());
    setEmailVerificationError(undefined);
    setIsDisabledVerifyButton(false);
    return response.payload;
  };

  const onNextStep = (handleSubmit) => {
    handleSubmit();
  };

  const handleCloseEmailVerificationModal = () => {
    setIsVerificationModalOpen(false);
    setEmailVerificationError(undefined);
    setIsDisabledVerifyButton(false);
  };

  const recaptchaRef = React.useRef<ReCAPTCHA>(null);

  return (
    <div className={`${styles["step"]}`}>
      <div className="row">
        <div className={`${styles["step-text"]} col-lg-6`}>
          <div className={styles["step-number"]}>
            <span>1</span>
          </div>
          <h2 className={`${styles["stepTitle"]}`}>
            Let's <span className="orange-yellow-color">get started</span>
          </h2>
          <p>
            All are welcome to apply to all parts of the Podcast Fellowship,
            however the $15 stipend per podcast comes from a private foundation
            which only pays a stipend for Jewish people to encourage them to
            understand Judaism better.
          </p>
        </div>
        <div className="col-lg-6">
          <Formik
            initialValues={initialValues}
            validationSchema={firstApplicationStepSchema()}
            onSubmit={async (values, { setSubmitting, setFieldValue }) => {
              await onNext({ ...values, referral_token: referralToken });
              recaptchaRef.current?.reset();
              setFieldValue("g_recaptcha_response", "");
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
              isSubmitting,
            }: /* and other goodies */
            any) => (
              <div className={`${styles["mobile-form"]}`}>
                <div className={`${styles["formRow"]} row`}>
                  <div className="col-lg">
                    <Input
                      label="First Name *"
                      name="fname"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.fname}
                      error={errors.fname && touched.fname ? "Required" : null}
                    />
                  </div>
                  <div className="col-lg">
                    <Input
                      label="Last Name *"
                      name="lname"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.lname}
                      error={errors.lname && touched.lname ? "Required" : null}
                    />
                  </div>
                </div>
                <div className={`${styles["formRow"]} row`}>
                  <div className="col-lg">
                    <Input
                      label="Email *"
                      name="email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                      error={
                        errors.email && touched.email ? errors.email : null
                      }
                    />
                    {/* {registerErrorMessage && (
                      <div className={`${styles["errorMessage"]}`}>
                        {registerErrorMessage}
                        <Link to={"/login"}> Login</Link>
                      </div>
                    )} */}
                  </div>
                  <div className="col-lg">
                    <PhoneInput
                      label="Phone Number *"
                      name="phone"
                      onChange={(phone) => {
                        setFieldValue("phone", phone);
                      }}
                      onBlur={handleBlur}
                      value={values.phone}
                      preferredCountries={["US", "CA"]}
                      error={
                        errors.phone && touched.phone ? errors.phone : null
                      }
                    />
                  </div>
                </div>
                <div className={`${styles["formRow"]} row`}>
                  <div className="col-lg">
                    <InputSelect
                      options={genderSelectOptions}
                      label="Gender *"
                      placeholder="Please select:"
                      name="gender"
                      onChange={({ value }: any) => {
                        setFieldValue("gender", value);
                      }}
                      onBlur={handleBlur}
                      value={values.gender}
                      error={
                        errors.gender && touched.gender ? "Required" : null
                      }
                    />
                  </div>
                  <div className="col-lg">
                    <DatePickerInput
                      label="Date of Birth *"
                      placeholder="mm/dd/yyyy"
                      name="dob"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.dob}
                      error={errors.dob && touched.dob ? errors.dob : null}
                    />
                  </div>
                </div>

                <div className={`${styles["formRow"]} row`}>
                  <div className="col-lg">
                    <InputSelect
                      options={countries}
                      label="Country (Current Residence) *"
                      name="country"
                      onChange={({ value }: any) => {
                        setFieldValue("country", value);
                      }}
                      onBlur={handleBlur}
                      value={values.country}
                      error={
                        errors.country && touched.country ? "Required" : null
                      }
                    />
                  </div>
                </div>

                <div className={`${styles["formRow"]} row`}>
                  <div className="col-lg">
                    <Input
                      label="Hometown (if different than current residence)"
                      name="city_residence"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.city_residence}
                    />
                  </div>
                  <div className="col-lg">
                    <Input
                      label="Home state (if different than current residence)"
                      name="state_residence"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.state_residence}
                    />
                  </div>
                </div>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITEKEY || ""}
                  onChange={(value) => {
                    setFieldValue("g_recaptcha_response", value ?? "");
                  }}
                  onExpired={() => {
                    setFieldValue("g_recaptcha_response", "");
                  }}
                  name="g_recaptcha_response"
                />
                {errors.g_recaptcha_response && (
                  <div className={styles["errorMessage"]}>
                    {errors.g_recaptcha_response}
                  </div>
                )}
                <div className={`${styles["formRow"]} row`}>
                  <div className="col-lg mt-0"></div>
                  <div className="col-lg mt-0">
                    <Button
                      name="Next step"
                      onClick={() => onNextStep(handleSubmit)}
                    />
                  </div>
                </div>
              </div>
            )}
          </Formik>
        </div>
        <EmailInUseModal
          isOpen={isEmailInUseModalOpen}
          onOk={() => setIsEmailInUseModalOpen(false)}
        />
        {isVerificationModalOpen && (
          <EmailVerificationModal
            onVerify={handleVerifyEmailCode}
            onResend={handleResendEmailCode}
            errorMessage={emailVerificationError}
            onClose={handleCloseEmailVerificationModal}
            isDisabledVerifyButton={isDisabledVerifyButton}
          />
        )}
      </div>
    </div>
  );
}

export default FirstStep;
